import queryString from 'query-string'
import React, { useContext } from 'react'
import { pick, camelCase, assign, each } from 'lodash'
import tracking, { useTracking } from 'react-tracking'
import Bowser from 'lib/bowser'
import Cookies from 'js-cookie'
import md5 from 'blueimp-md5'

import {
  settingsYourOrdersPath,
  registryGiftTrackerPath,
  genericProductsPath,
} from 'lib/urls'
import * as transform from './event-props'
import * as Avo from './__generated__/BabylistAvo'

const {
  ReactTrackingContext,
} = require('react-tracking/build/withTrackingComponentDecorator')

/**
 * extract system props (persistent and send with all event in the app -- do not survive reload / navigate page)
 * UMTs, landingPage, referer
 * UTM special case: extract UTMs from params, store in local session (and cookies) to be restore on each app load
 */
const gatherSystemProperties = () => {
  if (typeof document === 'undefined') return {}
  if (typeof window === 'undefined') return {}
  const utm = {}
  const params = queryString.parse(window.location.search)
  const utmParams = [
    'utm_campaign',
    'utm_medium',
    'utm_source',
    'utm_content',
    'utm_term',
    'utm_coop',
    'utm_group',
  ]
  utmParams.forEach((e) => {
    // store in local storage if defined in params
    if (params[e]) {
      const value = params[e]
      utm[camelCase(e)] = params[e]
      // how long do we want this to persist?
      localStorage.setItem(e, params[e])
      // store UTM in cookies (to be grabbed by shopify) for 1 week
      Cookies.set(e, value, { expires: 7, path: '/', domain: 'babylist.com' })
    } else {
      // restore from local storage if present
      const value = localStorage.getItem(e)
      if (value) {
        utm[camelCase(e)] = value
      }
    }
  })

  const browser = Bowser.parse(window.navigator.userAgent)
  const platform = `${browser.os.name} ${browser.os.version}`
  const deviceType = browser.platform?.type?.toLowerCase()
  const library = Avo.Library.WEB

  return {
    ...utm,
    deviceType,
    library,
    platform,
    landingPage: window.location.href,
    referrer: document.referrer,
  }
}

// Custom Destination allow us to have a special destination which will be handle here
// Use this destination to track all events to a HTTP bucket while testing
const customAvoLogger = {
  make(env) {
    // call when Avo create the custom destination
    // console.log('[avo/custom] make', env)
  },
  logEvent(eventName, eventProperties) {
    // call when Avo send metrics to custom destination
    // console.log('[avo/custom] logEvent', eventName, eventProperties)
    /* ex.:
    you can send the event to a pipedream bucket to store all events send
    for this session
    fetch(
      `https://bc51f0aeee7fdc79049544cdc0ab1dd6.m.pipedream.net/${eventName}`,
      {
        method: 'post',
        body: JSON.stringify(eventProperties)
      }
    )
    */
  },
  setUserProperties(userId, userProperties) {
    // call when Avo try to set some user props on custom destination
    // console.log('[avo/custom] setUserProperties', userId, userProperties)
    /*
    fetch(
      `https://bc51f0aeee7fdc79049544cdc0ab1dd6.m.pipedream.net/users/${userId}`,
      {
        method: 'put',
        body: JSON.stringify(userProperties)
      }
    );
    */
  },
  identify(userId) {
    // call when Avo identify user on custom destination
    // console.log('[avo/custom] identify', userId)
    /*
    fetch(
      `https://bc51f0aeee7fdc79049544cdc0ab1dd6.m.pipedream.net/identify/${userId}`,
      {
        method: 'post'
      }
    );
    */
  },
  unidentify() {
    // call when Avo unidentify user on custom destination
    // console.log('[avo/custom] unidentify')
    /*
    fetch(
      `https://bc51f0aeee7fdc79049544cdc0ab1dd6.m.pipedream.net/unidentify/${userId}`,
      {
        method: 'post'
      }
    );
    */
  },
}

/**
 * Call this somewhere that it is globally initialized for
 * all react apps or pages that need analytics tracking.
 */
const defaultInitOptions = {
  env: 'dev',
  strict: false,
  inspectorEnv: 'dev',
  systemProperties: {},
  destinationProperties: {},
}

export const initAnalytics = (options) => {
  const {
    systemProperties,
    destinationProperties,
    inspectorEnv,
    branchId,
    appVersion,
    ...restOptions
  } = { ...defaultInitOptions, ...options }

  // Require inspector here because importing the module tries to
  // access `window` which doesn't work in environments like jsdom.
  // In general there is some weird edge cases with importing this module
  // so do some extra testing if you change this (test IE (dev and canary),
  // run yarn test, test canary).
  // eslint-disable-next-line global-require
  const Inspector = require('./inspector').default
  let inspector
  if (!restOptions.noop) {
    inspector = new Inspector.AvoInspector({
      apiKey: Avo.avoInspectorApiKey,
      env: inspectorEnv,
      version: '1.0.0',
      appName: 'Babylist Web',
    })
  }

  // set some custom batch size / flush (in dev)
  // inspector.setBatchSize(0);
  // inspector.setBatchFlushSeconds(1);

  Avo.initAvo(
    {
      ...restOptions,
      inspector,
    },
    { branchId, appVersion, ...systemProperties, ...gatherSystemProperties() },
    destinationProperties,
    customAvoLogger
  )
}

const defaultEventProps = {
  eventLocation: Avo.EventLocation.NONE,
  eventType: Avo.EventType.NONE,
  eventCta: 'None', // this is not a restricted list so no ENUM
}

let userIdentified = false
let giftGiverIdentified = false

/**
 * trigger a userIdentified API call IF user HAS NOT already been identified
 * this logic exist to deal with user already logged in before new metrics system in place
 * as they won't do a login event to be associated, we need to do this here before
 * sending any event
 * Also as Segment charges us per event, we want to do it only once...
 * If user log out and then relogin, the identify call will be trigger at login time
 * so it's ok to set cookie forever (1 year)
 */
const identifyUser = () => {
  if (typeof document === 'undefined') return
  if (typeof window === 'undefined') return

  // identity user on analytics (once per year max)
  const userIdentifiedCookieName = 'userIdentifiedEventSent'
  if (!userIdentified) {
    userIdentified = true
    // if we have a user do a direct AVO call to identify
    if (window?.currentUser) {
      if (Cookies.get(userIdentifiedCookieName) === undefined) {
        Avo.userIdentified({ userId_: `${window.currentUser.id}` })
        Cookies.set(userIdentifiedCookieName, true, { expires: 365 })
      }
    }
  }

  // "identity" gift giver on analytics (once per page max)
  if (!giftGiverIdentified && !window?.currentUser) {
    giftGiverIdentified = true
    const giftGiverEmail = Cookies.get('email')
    // if we have a gift giver cookie set a predeterministic (segment) anonymous id
    if (giftGiverEmail !== undefined) {
      // eslint-disable-next-line no-unused-expressions
      window?.analytics?.ready(function () {
        // no impact in doing this over and over again.. // local storage
        window.analytics.user().anonymousId(`BL-${md5(giftGiverEmail)}`)
      })
    }
  }
}

/**
 * Set default (global) props
 *
 *
 * eventProperties are available for any event.
 * eventLocation, eventCta and eventType
 * ie.: eventLocation can be set an App level instead of settings it on each event
 *
 * Usage:
 *  1. `import { setDefaultEventProps } from 'lib/analytics'`
 *  2. you can set some default props (at app level) -- already set for registry/cart/home -> `setDefaultEventProps({eventLocation: track.EventLocation.HOME_PAGE})`
 *    - all future event will include `{eventLocation: 'PDP'}`, but can be overriden at tracking time: `track.addToRegistry(eventLocation: 'abc')`
 *    - you can opt out or select default props to be included:
 *      - `track.addToRegistry({blah: '123'}, [])`: will not send any set default
 *      - `track.addToRegistry({blah: '123'}, ['eventLocation'])`: will only add `eventLocation` default
 *
 * @see track
 */
export const setDefaultEventProps = (props) => assign(defaultEventProps, props)

// wrapping Avo in a proxy to handle setting default properties for event..
const trackHandler = {
  get(target, propKey, receiver) {
    try {
      const origMethod = target[propKey]
      if (typeof origMethod === 'function') {
        return function (...args) {
          // check if current user have already been identified
          identifyUser()
          // current event props
          const eventProps = args[0]
          // allow track.event({registry}) -> track.event({registryId: 123, registryOwnerId: 123, isRegistryOwner: true})
          const {
            registry,
            product,
            regItem,
            post,
            cartItem,
            user,
            reservation,
            newCartItem,
            cartItems,
            ...rest
          } = eventProps
          // TODO: use deepmerge!
          const spreadedEventProps = {
            // {...{ a: 1 }, ...{ a: undefined }} => { a: undefined}
            // merge({ a: 1 }, { a: undefined }) => { a: 1}
            // we spread the default props for each normalize objects
            ...transform.postEventProps(post),
            ...transform.registryEventProps(registry),
            ...transform.productEventProps(product),
            ...transform.regItemEventProps(regItem),
            ...transform.cartItemsEventProps(cartItems),
            ...transform.cartItemEventProps(cartItem),
            ...transform.newCartItemEventProps(newCartItem), // refactor
            ...transform.reservationEventProps(reservation),
            ...transform.userEventProps(user),
            // we spread the remaining event props
            ...rest,
          }
          // opt-in to inject default props (default behavior)
          const pickedProps = args[1]
          // if opt-out, select only desired default
          const pickedDefaultEventProps =
            pickedProps === undefined
              ? defaultEventProps
              : pick(defaultEventProps, ...pickedProps)
          // extend event props with default
          const extendedEventProps = {
            ...pickedDefaultEventProps,
            ...spreadedEventProps,
          }
          // call original method
          const result = origMethod.apply(this, [extendedEventProps])
          // console.log('[avo/track] ', propKey, extendedEventProps);
          return result
        }
      }
      return origMethod
    } catch (e) {
      console.log('tracking error', e)
      // if error we still returning an empty function...
      return (...args) => {}
    }
  },
}

/**
 * Public interface.
 *
 * Proxy Object around Avo, to handle defaultEventProps
 *
 * @type {Avo}
 * @see setDefaultEventProps
 * @see {@link https://www.avo.app/docs/commands}
 */
// eslint-disable-next-line import/no-mutable-exports
let track
try {
  track = new Proxy(Avo, trackHandler)
} catch (e) {
  // drop proxy if not available (IE <= 11)
  // we "just" loosing some default props
  track = Avo
}

/**
 * dummy tracking
 *
 * Just a safety dummy tracking object to be set as default props
 * Any highly re-used component (like Button, Link) should have this dummyTracking
 * as a default props to ensure not error would be raise if the component is included
 * in an hierarchy where no context have been defined..
 * this will log an error
 *
 */
export const dummyTracking = {
  trackEvent: () => {},
  getTrackingData: () => ({}),
}

/**
 * withContextualizedTracking
 *
 * Top level metrics HOC (App) which is in charge of dispatching any event
 * to Avo SDK
 *
 * usage:
 * ```
 * class MyApp extend Component {}
 * export default withContextualizedTracking({eventLocation: 'MyApp'})(MyApp)
 * ```
 *
 * @param {props} Hash - top level default props
 */
const withContextualizedTracking = (props = {}) => {
  return tracking(props, {
    dispatch: (data) => {
      try {
        const { event, events = [], ...props } = data
        const eventPropsWithUrl = { ...props, eventUrl: window.location.href } // default props on ALL event
        if (event) {
          events.push(event)
        }
        each(events, (event) => {
          if (event === undefined || typeof event !== 'function') {
            return
          }
          event(eventPropsWithUrl)
        })
      } catch (e) {
        console.log('tracking error', e, props)
      }
      // (window.dataLayer = window.dataLayer || []).push(data);
    },

    /*
      // track component mount
      dispatchOnMount: data => {
        console.log('dispatchOnMount', data);
        const { event, dispatchOnMount, ...props } = data;
        // console.log('event', event, props);
        if(dispatchOnMount) {
          console.log('tracking view');
          event(props);
        }
      }
      */

    /*
      // only process event if we have an event
      // https://github.com/nytimes/react-tracking#top-level-optionsprocess
      // todo check if any issue (if no event it cancel in the context of promise?)
      process: (data) => data.event ? data : null
      */
  })
}

// React tracking expect a ReactTrackingContext to be defined
// default behavior raise.. In our case we just return a no-op if no context
const useSafeTracking = () => {
  const trackingContext = useContext(ReactTrackingContext)

  if (!(trackingContext && trackingContext.tracking)) {
    console.log('missing tracking context')
    return {
      getTrackingData: () => ({}),
      trackEvent: () => {},
    }
  }

  // eslint-disable-next-line react-hooks/rules-of-hooks
  return useTracking()
}

const isTestEnv = process.env.NODE_ENV === 'test'
// mocking the withContextualizedTracking HOC to do nothing (return the comp)
const Identity = (Component) => Component
// mocking the @tracking decorator to NOT decorate the method
const IdentityTracking = () => {
  return function decorator(...rest) {
    if (rest.length === 1) {
      const [cls] = rest
      cls.defaultProps = {
        ...cls.defaultProps,
        tracking: {
          trackEvent: () => {},
        },
      }
      // decorating a class
      return cls
    } // decorating a method
    return rest[0].initializer
  }
}

const exportedTrack = isTestEnv ? Avo : track
const exportedTracking = isTestEnv ? IdentityTracking : tracking
const exportedUseTracking = isTestEnv ? () => dummyTracking : useSafeTracking
const exportedWithContextualizedTracking = isTestEnv
  ? () => Identity
  : withContextualizedTracking

export {
  exportedTrack as track,
  exportedTracking as tracking,
  exportedUseTracking as useTracking,
  exportedWithContextualizedTracking as withContextualizedTracking,
}

export const getEventLocationFromPath = (pathname) => {
  switch (pathname) {
    case settingsYourOrdersPath:
      return exportedTrack.EventLocation.ORDER_HISTORY
    case registryGiftTrackerPath:
      return exportedTrack.EventLocation.GIFT_TRACKER
    default:
      // eslint-disable-next-line no-case-declarations
      const matcher = new RegExp(`^${genericProductsPath}`)
      if (matcher.test(pathname)) {
        return exportedTrack.EventLocation.PDP
      }
      return exportedTrack.EventLocation.NONE
  }
}
